import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useAuth, type CaptchaFields } from '../lib/auth';
import { Captcha } from '../components/Captcha';
import { SiteFooter } from '../components/Layout';
import { TextInput } from '../components/ui';
import { readRememberedSignIn, saveRememberedSignIn } from '../lib/rememberMe';

/** What the panel beside the form argues, four claims at a time. */
const SELLING_POINTS = [
  {
    icon: '▤',
    title: 'Billing that moves stock',
    detail: 'Real-time inventory and GST invoices',
  },
  {
    icon: '⇄',
    title: 'Procurement that re-prices',
    detail: 'A goods receipt re-weights averages',
  },
  { icon: '₹', title: 'Filing-ready GST', detail: 'B2B, B2CS and HSN summaries' },
  { icon: '⌂', title: 'Multi-property', detail: 'Role-based access and full audit trail' },
] as const;

export function SignIn({
  offlineAvailable = false,
  onUseOfflineInstead,
}: {
  /** Phase 8: this browser has completed offline enrolment (OfflineSetup.tsx)
   *  and can actually back it - App.tsx already routes straight to
   *  OfflineUnlock.tsx while offline, so this only ever matters for the
   *  "browser reports online but the server itself is unreachable" case. */
  offlineAvailable?: boolean;
  onUseOfflineInstead?: () => void;
} = {}) {
  const { signIn, signedOutReason, status } = useAuth();
  // Read once: the address a "Remember me" sign-in left behind, and the box's own state.
  const [remembered] = useState(readRememberedSignIn);
  const [email, setEmail] = useState(remembered.email);
  const [rememberMe, setRememberMe] = useState(remembered.remember);
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState<CaptchaFields>({});
  const [captchaError, setCaptchaError] = useState<string | undefined>();
  // Bumped after every attempt: a challenge is single-use, so the one on screen
  // is already spent by the time an error comes back.
  const [captchaToken, setCaptchaToken] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set once the server says this account has two-step verification on. The
  // password stays in state and is sent again with the code: the server has to
  // check both together, and it will not say a code is wanted until the
  // password is right.
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState('');

  if (status === 'authenticated') return <Navigate to="/" replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setCaptchaError(undefined);
    try {
      await signIn(email.trim(), password, captcha, code.trim() || undefined, rememberMe);
      // Only after it worked, so a mistyped address is never what gets remembered.
      saveRememberedSignIn(rememberMe, email.trim());
    } catch (caught) {
      const code_ = caught instanceof ApiError ? caught.code : '';
      const isCaptcha = code_ === 'CAPTCHA_INVALID' || code_ === 'CAPTCHA_REQUIRED';

      if (code_ === 'TOTP_REQUIRED') {
        // First time round: the password was right, and now the code is wanted.
        setNeedsCode(true);
        setCode('');
      } else if (code_ === 'TOTP_INVALID') {
        setNeedsCode(true);
        setCode('');
        setError((caught as ApiError).message);
      } else if (isCaptcha) {
        setCaptchaError((caught as ApiError).message);
      } else {
        // Anything else sends them back to the start, including a lockout: a
        // code field left on screen for an account that is locked is a lie.
        setNeedsCode(false);
        setCode('');
        setError(
          caught instanceof ApiError
            ? caught.message
            : 'Could not sign in. Check your connection and try again.',
        );
      }
      // Either way the challenge is spent; pull a fresh one.
      setCaptchaToken((token) => token + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell">
      <aside className="auth-aside">
        <div className="auth-brand">
          <div className="brand-mark">S</div>
          <div>
            <div className="brand-name">SARTHI</div>
            <div className="brand-strap">
              Smart Automated Retail Transaction &amp; Hospitality Infrastructure
            </div>
          </div>
        </div>

        <div className="auth-sectors">
          {['Hotels', 'Restaurants', 'Cafés', 'Resorts', 'Retail'].map((sector) => (
            <span key={sector}>{sector}</span>
          ))}
        </div>

        <div>
          <h1>One system from the purchase order to the payslip.</h1>
          <span className="auth-rule" aria-hidden="true" />
          <p>
            Restaurant billing with GST, stock that depletes itself from recipes, cost-led menu
            pricing, procurement, payroll and the front desk — across every property you run.
          </p>
        </div>

        <div className="auth-features">
          {SELLING_POINTS.map((point) => (
            <div key={point.title}>
              <span className="feat-icon" aria-hidden="true">
                {point.icon}
              </span>
              <div>
                <b>{point.title}</b>
                <span>{point.detail}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="auth-trust">
          <p>Trusted by hotels, restaurants and retail businesses across India.</p>
          <div className="trust-line">
            <span className="auth-rule sm" aria-hidden="true" />
            {['Simpler Operations', 'Higher Profits', 'Happier Guests'].map((claim) => (
              <span key={claim}>{claim}</span>
            ))}
          </div>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-card">
          <h2>Sign in</h2>
          <div className="sub">Use the account your manager set up for you.</div>

          <form onSubmit={submit} className="card">
            <div className="card-body col gap-16">
              {error ? (
                <div className="form-error">{error}</div>
              ) : signedOutReason ? (
                <div className="form-note">{signedOutReason}</div>
              ) : null}

              <TextInput
                label="Email"
                type="email"
                autoComplete="username"
                required
                autoFocus={!remembered.email}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@property.in"
              />
              <TextInput
                label="Password"
                type="password"
                autoComplete="current-password"
                required
                autoFocus={Boolean(remembered.email)}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <div className="col gap-4">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    aria-describedby="remember-me-hint"
                  />
                  <b>Remember me</b>
                </label>
                {/* Outside the label on purpose: the checkbox is named by its label, and
                    a hint that mentions the email field would also match a lookup for it. */}
                <span id="remember-me-hint" className="small muted">
                  Stay signed in on this device and keep my address filled in. Leave it off on a
                  shared one.
                </span>
              </div>

              {needsCode ? (
                <TextInput
                  label="Authenticator code"
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  hint="The six digits from your authenticator app, or one of your recovery codes."
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              ) : null}

              <Captcha
                value={captcha}
                onChange={setCaptcha}
                error={captchaError}
                disabled={busy}
                reloadToken={captchaToken}
              />

              <button className="btn primary lg block" type="submit" disabled={busy}>
                {busy ? 'Signing in…' : needsCode ? 'Verify and sign in' : 'Sign in'}
              </button>

              <div className="center small muted">
                <Link to="/forgot-password">Forgot your password?</Link>
              </div>

              <div className="center small muted">
                New here? <Link to="/register">Register your hotel</Link> — free for 30 days.
              </div>

              {offlineAvailable && onUseOfflineInstead ? (
                <div className="center small muted">
                  Can't reach the server?{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={onUseOfflineInstead}
                    style={{ background: 'none', border: 0, padding: 0, textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Sign in offline instead
                  </button>
                </div>
              ) : null}
            </div>
          </form>
        </div>

        <SiteFooter />
      </main>
    </div>
  );
}
